import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import axios from "axios";
import { LOGOS } from "../../lib/ablemp-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Notícia: "#002776",
  Artigo: "#009B3A",
  Manifesto: "#0A0F14",
  Posicionamento: "#475569",
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const coverUrl = (n) =>
  n?.cover_name ? `${API}/public/news/${n.id}/cover` : null;

const PublicHeader = () => (
  <header className="bg-[#0A0F14] border-b border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3">
        <img src={LOGOS.white} alt="ABLEMP" className="h-12 md:h-14 w-auto" />
      </Link>
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-medium text-white/85 hover:text-[#FEDF00] transition-colors"
      >
        <ArrowLeft size={14} /> Voltar ao site
      </Link>
    </div>
  </header>
);

export default function NoticiasEArtigos() {
  const { slug } = useParams();
  if (slug) return <NewsDetail slug={slug} />;
  return <NewsList />;
}

function NewsList() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("Todos");

  useEffect(() => {
    document.title = "Notícias e Artigos · ABLEMP";
    axios
      .get(`${API}/public/news`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  const categories = ["Todos", "Notícia", "Artigo", "Manifesto", "Posicionamento"];
  const filtered =
    filter === "Todos" ? items : (items || []).filter((n) => n.category === filter);

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0B1015]">
      <PublicHeader />

      <section className="bg-[#0A0F14] text-white pt-16 pb-20 md:pb-24 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FEDF00]">
            § Publicações
          </div>
          <h1 className="mt-4 font-serif-display text-[36px] sm:text-[48px] lg:text-[68px] leading-[1.05] tracking-[-0.02em] max-w-4xl">
            Notícias e <span className="italic font-light text-white/80">Artigos</span>
          </h1>
          <p className="mt-5 text-white/70 max-w-2xl text-base md:text-lg leading-relaxed">
            Posicionamentos, análises e notícias publicadas pela ABLEMP em defesa
            da liberdade de escolha dos meios de pagamento.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`news-filter-${c.toLowerCase()}`}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] border rounded-sm transition-colors ${
                  filter === c
                    ? "bg-[#0A0F14] text-white border-[#0A0F14]"
                    : "bg-white text-[#0B1015] border-[#0B1015]/20 hover:border-[#002776]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {items === null ? (
            <div className="text-sm text-slate-500">Carregando publicações...</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-[#0B1015]/20 p-14 text-center">
              <div className="font-serif-display text-2xl">
                Nenhuma publicação ainda
              </div>
              <p className="mt-2 text-sm text-[#475569]">
                Quando o administrador da ABLEMP publicar uma matéria, ela
                aparecerá aqui.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((n) => (
                <NewsCard key={n.id} n={n} />
              ))}
            </div>
          )}
        </div>
      </section>

      <footer className="bg-[#0A0F14] text-white/60 py-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center text-xs uppercase tracking-[0.3em]">
          ABLEMP · Defesa da Liberdade dos Meios de Pagamento
        </div>
      </footer>
    </div>
  );
}

const NewsCard = ({ n }) => {
  const url = coverUrl(n);
  const color = CATEGORY_COLORS[n.category] || "#0A0F14";
  return (
    <Link
      to={`/noticias-e-artigos/${n.slug}`}
      data-testid={`news-card-${n.slug}`}
      className="group block bg-white border border-[#0B1015]/12 hover:shadow-lg transition-shadow overflow-hidden"
    >
      {url ? (
        <div className="aspect-[16/9] overflow-hidden bg-slate-100">
          <img
            src={url}
            alt={n.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      ) : (
        <div
          className="aspect-[16/9] grid place-items-center"
          style={{ background: `${color}10`, color }}
        >
          <span className="text-[10px] uppercase tracking-[0.3em]">
            {n.category}
          </span>
        </div>
      )}
      <div className="p-6 md:p-7">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em]">
          <span
            className="px-2 py-0.5 border"
            style={{
              borderColor: `${color}40`,
              color,
            }}
          >
            {n.category}
          </span>
          <span className="text-[#475569]">{formatDate(n.published_at)}</span>
        </div>
        <h3 className="mt-4 font-serif-display text-xl md:text-[22px] leading-tight line-clamp-3">
          {n.title}
        </h3>
        {n.summary && (
          <p className="mt-3 text-sm text-[#475569] leading-relaxed line-clamp-3">
            {n.summary}
          </p>
        )}
        <div className="mt-5 flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#0B1015] editorial-link group-hover:active">
            Ler matéria
          </span>
          <ArrowUpRight
            size={18}
            className="text-[#0B1015] group-hover:text-[#002776] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
          />
        </div>
      </div>
    </Link>
  );
};

function NewsDetail({ slug }) {
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/public/news/${slug}`)
      .then((r) => {
        setItem(r.data);
        document.title = `${r.data.title} · ABLEMP`;
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F4F4F0] text-[#0B1015]">
        <PublicHeader />
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center">
          <h1 className="font-serif-display text-3xl">
            Publicação não encontrada
          </h1>
          <p className="mt-3 text-sm text-[#475569]">
            O conteúdo procurado pode ter sido removido ou ainda não foi
            publicado.
          </p>
          <button
            onClick={() => navigate("/noticias-e-artigos")}
            className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
          >
            Ver todas as publicações
          </button>
        </div>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="min-h-screen bg-[#F4F4F0] text-[#0B1015]">
        <PublicHeader />
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-sm text-[#475569]">
          Carregando publicação...
        </div>
      </div>
    );
  }

  const url = coverUrl(item);
  const color = CATEGORY_COLORS[item.category] || "#0A0F14";

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0B1015]">
      <PublicHeader />
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-20">
        <Link
          to="/noticias-e-artigos"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#475569] hover:text-[#002776]"
        >
          <ArrowLeft size={14} /> Voltar para publicações
        </Link>
        <div className="mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em]">
          <span
            className="px-2 py-1 border"
            style={{ borderColor: `${color}40`, color }}
          >
            {item.category}
          </span>
          <span className="text-[#475569]">
            {formatDate(item.published_at)}
          </span>
          {item.author && (
            <span className="text-[#475569]">· por {item.author}</span>
          )}
        </div>
        <h1 className="mt-4 font-serif-display text-[34px] md:text-[52px] leading-[1.05] tracking-[-0.02em]">
          {item.title}
        </h1>
        {item.summary && (
          <p className="mt-5 text-lg text-[#475569] leading-relaxed border-l-2 border-[#002776] pl-5">
            {item.summary}
          </p>
        )}
        {url && (
          <div className="mt-10 -mx-6 md:mx-0">
            <img
              src={url}
              alt={item.title}
              className="w-full max-h-[520px] object-cover"
            />
          </div>
        )}
        {item.content && (
          <div className="mt-10 prose prose-lg max-w-none whitespace-pre-line text-[16px] leading-[1.75] text-[#1f2937]">
            {item.content}
          </div>
        )}
        {item.source_url && (
          <div className="mt-10 pt-6 border-t border-[#0B1015]/15">
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#002776] hover:text-[#0A0F14]"
            >
              <ExternalLink size={14} /> Fonte original
            </a>
          </div>
        )}
      </article>
      <footer className="bg-[#0A0F14] text-white/60 py-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center text-xs uppercase tracking-[0.3em]">
          ABLEMP · Defesa da Liberdade dos Meios de Pagamento
        </div>
      </footer>
    </div>
  );
}
